import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { ThemeSwitcher } from '../components/ThemeSwitcher';
import { HumanCheck, humanCheckEnabled } from '../components/HumanCheck';
import { api } from '../lib/api';
import { useAuth, type SessionUser } from '../lib/auth';
import { useAction } from '../lib/useApi';
import { ErrorBox, Field, Notice, PasswordInput } from '../components/ui';

type Mode = 'signin' | 'signup' | 'magic';

export function AuthPage({ initial = 'signin' }: { initial?: Mode }) {
  const [mode, setMode] = useState<Mode>(initial);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [accountType, setAccountType] = useState<'parent' | 'tutor'>('parent');
  const [humanToken, setHumanToken] = useState<string | null>(null);
  const [adult, setAdult] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);
  const { signIn } = useAuth();
  const nav = useNavigate();
  const act = useAction();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'magic') {
      const r = await act.run(() => api<{ devLink?: string }>('/auth/magic-link', { body: { email }, token: null }), 'If that email has an account, a sign-in link is on its way.');
      setDevLink(r?.devLink ?? null);
      return;
    }
    const r = await act.run(() =>
      mode === 'signin'
        ? api<{ token: string; user: SessionUser }>('/auth/login', { body: { email, password }, token: null })
        : api<{ token: string; user: SessionUser }>('/auth/signup', {
            body: { email, password, displayName, familyName: familyName || undefined, accountType, turnstileToken: humanToken ?? undefined },
            token: null,
          }),
    );
    if (r) {
      signIn(r.token, r.user);
      nav(r.user.role === 'tutor' ? '/tutor' : r.user.role === 'safeguarding_admin' || r.user.role === 'content_editor' ? '/admin' : mode === 'signup' ? '/consent' : '/');
    }
  };

  return (
    <div className="auth-wrap">
      {/* Appearance belongs here too, not only on the signed-in topbar: someone
          who finds the dark default hard to read must be able to change it
          before they have an account, not after. */}
      <div className="auth-appearance">
        <ThemeSwitcher />
      </div>
      <section className="auth-hero">
        <div className="wordmark">
          <h1 lang="yo">Ìran</h1>
          <span className="wordmark-by">by OmniAlera</span>
        </div>
        <p className="auth-promise">Keep your language alive. Keep your people close.</p>
        <p style={{ fontSize: '1.2rem', maxWidth: 540, opacity: 0.94 }}>
          Learn the language your family speaks. Preserve the voices of those who came before you. Pass the stories on.
        </p>
        <ul style={{ lineHeight: 2, paddingLeft: 18, opacity: 0.92 }}>
          <li>
            <strong>Learn</strong> — games and group lessons in Yorùbá for ages 4–17
          </li>
          <li>
            <strong>Preserve</strong> — a private vault for proverbs, oríkì, recipes and songs, in any of 94 languages
          </li>
          <li>
            <strong>Belong</strong> — a Voice Tree and a family timeline, four generations deep
          </li>
        </ul>
        <p className="small inline-icon" style={{ opacity: 0.8, marginTop: 24 }}>
          <ShieldCheck size={17} strokeWidth={1.75} aria-hidden="true" />
          <span>Built children-first: parental consent, no public profiles, no private messaging, group-only tutoring.</span>
        </p>
      </section>
      <section className="auth-panel">
        <div className="tabs" role="tablist">
          {(['signin', 'signup', 'magic'] as Mode[]).map((m) => (
            <button key={m} role="tab" aria-selected={mode === m} onClick={() => setMode(m)} type="button">
              {m === 'signin' ? 'Sign in' : m === 'signup' ? 'Create account' : 'Email me a link'}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="card" aria-label={mode}>
          {mode === 'signup' && (
            <>
              <div className="field">
                <span className="label">I am a…</span>
                <div className="row">
                  <label className="check">
                    <input type="radio" name="acct" checked={accountType === 'parent'} onChange={() => setAccountType('parent')} /> Parent or guardian
                  </label>
                  <label className="check">
                    <input type="radio" name="acct" checked={accountType === 'tutor'} onChange={() => setAccountType('tutor')} /> Tutor applicant
                  </label>
                </div>
              </div>
              <Field id="dn" label="Your first name">
                <input id="dn" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required autoComplete="given-name" />
              </Field>
              {accountType === 'parent' && (
                <Field id="fn" label="Family name (private)" hint="Only your family sees this. e.g. “The Adébáyọ̀ family”">
                  <input id="fn" value={familyName} onChange={(e) => setFamilyName(e.target.value)} className="yo" />
                </Field>
              )}
            </>
          )}
          <Field id="em" label="Email">
            <input id="em" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
          </Field>
          {mode !== 'magic' && (
            <Field id="pw" label="Password" hint={mode === 'signup' ? 'At least 10 characters' : undefined}>
              <PasswordInput id="pw" value={password} onChange={setPassword} required minLength={mode === 'signup' ? 10 : 1} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} />
            </Field>
          )}
          {mode === 'signup' && (
            <label className="check">
              <input type="checkbox" checked={adult} onChange={(e) => setAdult(e.target.checked)} required />
              <span>I am 18 or over. Children never create their own accounts — a parent adds them after giving verified consent.</span>
            </label>
          )}
          {/* Only on sign-up. Sign-in is rate-limited per account and per
              address, and putting a challenge in front of a parent every single
              time they come back would cost them far more than it costs an
              attacker. */}
          {mode === 'signup' && <HumanCheck onToken={setHumanToken} />}
          <ErrorBox error={act.error} />
          <Notice text={act.notice} />
          {devLink && (
            <p className="small">
              Dev mode: <a href={devLink.replace(/^https?:\/\/[^/]+/, '')}>open the magic link</a>
            </p>
          )}
          <button className="btn" type="submit" disabled={act.busy || (mode === 'signup' && (!adult || (humanCheckEnabled && !humanToken)))} style={{ width: '100%' }}>
            {act.busy ? 'Please wait…' : mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Send sign-in link'}
          </button>
          <p className="small muted" style={{ marginTop: 14 }}>
            Your Ìran account is your own — it opens Ìran and nothing else. <Link to="/privacy-promise">Our privacy promise</Link>
          </p>
        </form>
      </section>
    </div>
  );
}

export function MagicLinkPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { signIn } = useAuth();
  const nav = useNavigate();
  const act = useAction();
  const [password, setPassword] = useState('');
  return (
    <div className="auth-panel" style={{ margin: '60px auto' }}>
      <form
        className="card"
        onSubmit={async (e) => {
          e.preventDefault();
          const r = await act.run(() => api<{ token: string; user: SessionUser }>('/auth/magic-link/verify', { body: { token, newPassword: password || undefined }, token: null }));
          if (r) {
            signIn(r.token, r.user);
            nav('/');
          }
        }}
      >
        <h1>Welcome to Ìran</h1>
        <p>You’re signing in with a one-time link.</p>
        <Field id="np" label="Set a password (optional)" hint="Grandparents can skip this and always sign in by email link.">
          <PasswordInput id="np" value={password} minLength={10} onChange={setPassword} autoComplete="new-password" />
        </Field>
        <ErrorBox error={act.error} />
        <button className="btn" disabled={act.busy || !token}>
          Continue
        </button>
      </form>
    </div>
  );
}

export function PrivacyPromise() {
  return (
    <div className="main" style={{ margin: '0 auto' }}>
      <h1>Our privacy promise to children and families</h1>
      <ol className="stack">
        <li>No child profile exists until a parent has given verified consent.</li>
        <li>No public profiles, followers, likes, leaderboards or search. Family content is visible only to your family.</li>
        <li>No private messaging — between anyone. Adults never message children through Ìran.</li>
        <li>Live Circles are always groups (at least 2 children), always recorded, and parents can always join.</li>
        <li>Tutors are identity-checked, reference-checked, background-checked where available, and safeguarding-trained.</li>
        <li>Children’s practice recordings are deleted automatically after the number of days you choose.</li>
        <li>Your family’s recordings are never used to train AI unless you explicitly opt in. Children’s practice clips — never.</li>
        <li>Anyone, including a child, can report a worry. Serious concerns suspend a tutor immediately.</li>
      </ol>
      <Link to="/signin">Back to sign in</Link>
    </div>
  );
}
